import {
  Inbox,
  Sparkles,
  CheckCircle2,
  HelpCircle,
  Ban,
  ListChecks,
  Wand2,
  FlaskConical,
  Plug,
} from "lucide-react";
import { useHive } from "@/store/useHive";
import { useSampleDataCta } from "@/hooks/useSampleDataCta";
import { APPS } from "@/data/apps";
import { AUTO_RESOLVE_LEVELS } from "@/data/tasks";

const accent = APPS.tasks.accent;

/** Compact desktop surface — the incoming task queue at a glance. */
export function TasksWidget() {
  const openApp = useHive((s) => s.openApp);
  const tasks = useHive((s) => s.tasks);
  const refineTask = useHive((s) => s.refineTask);
  const selectTask = useHive((s) => s.selectTask);
  const level = useHive((s) => s.autoResolveLevel);
  const setLevel = useHive((s) => s.setAutoResolveLevel);

  const sampleCta = useSampleDataCta();

  const incoming = tasks.filter((t) => !["done"].includes(t.status)).length;
  const ready = tasks.filter((t) => t.status === "ready").length;
  const clarify = tasks.filter((t) => t.status === "needs-clarification").length;
  const blocked = tasks.filter((t) => t.status === "blocked").length;
  const nextToRefine = tasks.find((t) => !t.refined);

  const stop = (e: React.PointerEvent) => e.stopPropagation();

  const cells = [
    { n: ready, l: "ready", icon: CheckCircle2, color: "#22c55e" },
    { n: clarify, l: "clarify", icon: HelpCircle, color: "#f59e0b" },
    { n: blocked, l: "blocked", icon: Ban, color: "#f43f5e" },
  ];

  // Fresh accounts: pull work in from integrations, or explore the demo set.
  if (tasks.length === 0) {
    return (
      <div className="w-[330px] cursor-pointer" onClick={() => openApp("tasks")}>
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs font-medium text-app-2">
            <Inbox size={14} style={{ color: accent }} /> Tasks
          </div>
          <span className="text-[11px] text-app-2 tabular-nums">0 incoming</span>
        </div>
        <div className="rounded-xl border border-dashed border-app bg-surface-2/40 p-4 text-center">
          <div className="text-sm font-semibold text-app">Your queue is clear</div>
          <div className="mt-1 text-xs text-app-2">
            Connect Jira, GitHub or Slack and Hive turns incoming work into agent-ready tasks.
          </div>
          <button
            onPointerDownCapture={stop}
            onClick={(e) => {
              e.stopPropagation();
              openApp("integrations");
            }}
            className="mt-3 inline-flex items-center justify-center gap-1 rounded-lg px-3 py-1.5 text-[11px] font-semibold text-black transition hover:brightness-105"
            style={{ background: accent }}
          >
            <Plug size={12} /> Connect a source
          </button>
          {sampleCta && (
            <button
              onPointerDownCapture={stop}
              onClick={(e) => {
                e.stopPropagation();
                sampleCta.run();
              }}
              className="mt-1.5 inline-flex items-center justify-center gap-1 rounded-lg border border-app px-3 py-1.5 text-[11px] font-medium text-app-1 transition hover:border-app-strong"
            >
              <FlaskConical size={12} /> {sampleCta.label}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="w-[330px] cursor-pointer" onClick={() => openApp("tasks")}>
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs font-medium text-app-2">
          <Inbox size={14} style={{ color: accent }} /> Tasks
        </div>
        <span className="text-[11px] text-app-2 tabular-nums">{incoming} incoming</span>
      </div>

      <div className="flex items-end gap-3">
        <div>
          <div className="text-4xl font-semibold leading-none text-app tabular-nums">{incoming}</div>
          <div className="mt-1 text-[11px] text-app-2">tasks in queue</div>
        </div>
        <div className="mb-0.5 flex-1 grid grid-cols-3 gap-1.5">
          {cells.map((c) => (
            <div key={c.l} className="rounded-lg border border-app bg-surface-2/40 py-1.5 text-center">
              <div className="flex items-center justify-center gap-1 text-sm font-semibold tabular-nums" style={{ color: c.color }}>
                <c.icon size={12} /> {c.n}
              </div>
              <div className="text-[9px] uppercase tracking-wide text-app-2">{c.l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* auto-resolve status */}
      <div className="mt-3 flex items-center justify-between rounded-xl border border-app bg-surface-2/40 px-3 py-2">
        <div className="flex items-center gap-2">
          <Sparkles size={13} style={{ color: accent }} />
          <span className="text-[11px] text-app-2">Auto-resolve</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-medium text-app">{AUTO_RESOLVE_LEVELS[level].name}</span>
          <button
            onPointerDownCapture={stop}
            onClick={(e) => {
              e.stopPropagation();
              setLevel(level >= 4 ? 0 : level + 1);
            }}
            className="grid h-5 w-9 place-items-start rounded-full p-0.5 transition"
            style={{ background: level > 0 ? accent : "hsl(var(--surface-2))" }}
            title="Cycle auto-resolve level"
          >
            <span
              className="h-4 w-4 rounded-full bg-white shadow transition-transform"
              style={{ transform: `translateX(${level * 4}px)` }}
            />
          </button>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-1.5">
        <button
          onPointerDownCapture={stop}
          onClick={(e) => {
            e.stopPropagation();
            openApp("tasks");
          }}
          className="flex items-center justify-center gap-1 rounded-lg border border-app bg-surface-2/60 py-1.5 text-[11px] font-medium text-app-1 transition hover:border-app-strong"
        >
          <ListChecks size={12} /> Review Queue
        </button>
        <button
          onPointerDownCapture={stop}
          onClick={(e) => {
            e.stopPropagation();
            if (nextToRefine) {
              selectTask(nextToRefine.id);
              refineTask(nextToRefine.id);
            }
            openApp("tasks");
          }}
          className="flex items-center justify-center gap-1 rounded-lg py-1.5 text-[11px] font-semibold text-black transition hover:brightness-105"
          style={{ background: accent }}
        >
          <Wand2 size={12} /> Refine Next
        </button>
      </div>
    </div>
  );
}
